import FilterIcon from '@assets/icons/filter.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { CATALOG_ROUTES } from '@/navigators/routes';
import { CatalogStackParams } from '@/navigators/types';
import { CategorySelector } from '@/components/partners/category-selector';
import { createStyles } from '@/contexts/theme-context';
import { GTINFilterActionSheet } from '@/components/gtin-filters/gtin-filter-action-sheet';
import { isPathInCategoryItemGroups } from '@/utils/category';
import { ItemGroupSelector } from '@/components/item-group-selector';
import { ItemSearchInput } from '@/components/item-search-input';
import { KeyboardAvoidingView, Platform, View } from 'react-native';
import { ModalContainer } from '@/components/modal-container';
import { PageContainer } from '@/components/page-container';
import { SearchCatalogContent } from '@/components/search-catalog-content';
import { StackScreenProps } from '@react-navigation/stack';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemGroupBadgeFilters } from '@/hooks/use-item-group-badge-filters';
import { useItemGroupCategoryFilters } from '@/hooks/use-item-group-category-filters';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSearchCatalog } from '@/hooks/use-search-catalog';
import { useUserContext } from '@/contexts/user-context';

export type Props = StackScreenProps<CatalogStackParams, CATALOG_ROUTES.Search>;

export const SearchSupplierCatalogScreen: React.FC<Props> = ({ route }) => {
  const { category, modalScreen, initialSearchText, includeGTINFilters } =
    route.params || {};

  const { isGuestMode } = useUserContext();
  const {
    badgesFilter,
    categories,
    categoryFilter,
    categoryItemGroups,
    categoryItemGroupFilter,
    changeSortOrder,
    clearSearch,
    items,
    queryLoading,
    queryRef,
    searchItems,
    searchSortOrder,
    setCategoryFilter,
    setCategoryItemGroupFilter,
    gtinFilters,
    setBadgesFilter,
  } = useSearchCatalog({
    category,
    includeGTINFilters,
    initialSearchText,
    searchSource: 'search_supplier_catalog',
  });
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');

  const { categoryItemGroupsInSelector } = useItemGroupCategoryFilters({
    categoryUUIDsToFilterFrom: categoryItemGroups.map(
      (itemGroup) => itemGroup.uuid,
    ),
    filterByCategoryType: !category,
    selectedCategoryUUID: category?.uuid,
  });
  const { badges } = useItemGroupBadgeFilters({
    category,
    categoryItemGroups,
    items: items ?? undefined,
  });

  const filtersAvailable = useMemo(() => {
    return (
      gtinFilters.listFilters.some((f) => f.isAvailable) ||
      gtinFilters.flagFilters.some((f) => f.isAvailable)
    );
  }, [gtinFilters]);

  const { isSmallLayout } = useLayoutContext();
  const [filterSheetVisible, setFilterSheetVisible] = useState(false);

  const [styles, theme] = useStyles();

  // if any of the categories in the filter are not in the categories array
  // the search no longer includes that category. So remove the selected category filter
  useEffect(() => {
    if (itemGroupsEnabled) {
      setCategoryItemGroupFilter((prevFilter) => {
        if (prevFilter) {
          const isFilterInCategoryItemGroups = isPathInCategoryItemGroups(
            categoryItemGroupsInSelector,
            prevFilter.path ?? '',
          );
          if (!isFilterInCategoryItemGroups) {
            return null;
          }
        }
        return prevFilter;
      });
    } else {
      setCategoryFilter((prevFilter) => {
        if (prevFilter && !categories.includes(prevFilter)) {
          return null;
        }
        return prevFilter;
      });
    }
  }, [
    categories,
    setCategoryFilter,
    setCategoryItemGroupFilter,
    categoryItemGroupsInSelector,
    itemGroupsEnabled,
  ]);

  // reset badges to an empty set if any of the previous badges are not included in current badges
  // requires ItemGroups to be enabled because badges are only used on ItemGroups
  useEffect(() => {
    if (itemGroupsEnabled) {
      setBadgesFilter((prevFilter) => {
        if (prevFilter) {
          const badgeUUIDs = new Set(badges.map((b) => b.uuid));
          const filteredSet = new Set(
            [...prevFilter].filter((filter) => badgeUUIDs.has(filter)),
          );

          return filteredSet.size === prevFilter.size ? prevFilter : new Set();
        }

        return prevFilter;
      });
    }
  }, [badges, setBadgesFilter, itemGroupsEnabled]);

  const isEmpty = items?.length === 0;

  const ContainerComponent = modalScreen ? ModalContainer : PageContainer;

  return (
    <ContainerComponent
      navigationHeaderProps={{
        actions:
          filtersAvailable || Boolean(badges.length)
            ? [
                {
                  Icon: FilterIcon,
                  iconProps: {
                    fill: undefined,
                    stroke: theme.colors.primary,
                  },
                  onPress: () => {
                    setFilterSheetVisible(true);
                  },
                  testID: 'filter-icon',
                },
              ]
            : [],
        showBackButton: true,
        showShadow: true,
        subheader: (
          <>
            <ItemSearchInput
              clearSearch={clearSearch}
              containerStyle={styles.searchInputContainer}
              initialSearchText={initialSearchText}
              loading={queryLoading}
              onSearchItems={searchItems}
              placeholder={i18n.t('search')}
            />
            <View style={styles.subheaderContainer}>
              {itemGroupsEnabled ? (
                <ItemGroupSelector
                  data={categoryItemGroupsInSelector}
                  onItemGroupPress={setCategoryItemGroupFilter}
                  selectedItemGroup={categoryItemGroupFilter}
                  trackingKey="search_screen.item_group_selector"
                />
              ) : (
                <CategorySelector
                  categories={categories}
                  onCategoryPress={setCategoryFilter}
                  selectedCategory={categoryFilter}
                  style={styles.categorySelectorContainer}
                />
              )}
            </View>
          </>
        ),
        title: modalScreen ? i18n.t('add_items_from_catalog') : undefined,
      }}
      shouldDismissKeyboard
    >
      <>
        <KeyboardAvoidingView
          behavior={Platform.select({ android: undefined, ios: 'padding' })}
          enabled={isEmpty}
          style={styles.keyboardAvoidingView}
        >
          <SearchCatalogContent
            changeSortOrder={isGuestMode ? undefined : changeSortOrder}
            items={items}
            loading={queryLoading}
            renderSource="search_supplier_catalog"
            searchSortOrder={searchSortOrder}
            searchText={queryRef.current}
          />
        </KeyboardAvoidingView>
        {filterSheetVisible && isSmallLayout ? (
          <GTINFilterActionSheet
            badges={badges}
            badgesFilter={badgesFilter}
            gtinFilters={gtinFilters}
            onClose={() => setFilterSheetVisible(false)}
            onSelectBadges={setBadgesFilter}
          />
        ) : null}
      </>
    </ContainerComponent>
  );
};

const useStyles = createStyles((theme) => ({
  categorySelectorContainer: {
    borderColor: theme.colors.stroke,
    borderTopWidth: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  searchInputContainer: {
    backgroundColor: theme.colors.background,
    marginBottom: 8,
    marginHorizontal: 16,
  },
  subheaderContainer: {
    flexDirection: 'row',
  },
}));
